import { ContentItemDTO, ContentKind, ContentKindInventoryDTO, VisualKeyDTO } from 'models/socialCampaign'

/**
 * The visuals of a kind of content, kept or left out (ISSUE-3651, prototype v3 `openBucket` / `renderBucket` /
 * `setBucket`): the kind is a bucket the posts are written from. No limit and no order; leaving every visual of a kind
 * out leaves the kind out, keeping one brings it back (the backend applies both); photos keep at least one.
 *
 * The dialog works on the viewer's content inventory of the kind, so what it sends is always a subset of it, as the
 * backend requires (`ContentKindVisualsDTO`): the campaign's `excludedVisuals` may also hold visuals the viewer's seat
 * does not show, or no longer delivered, which are never sent back.
 */

/** A kind's visuals as the dialog saves them: kept and left out, in delivery order. */
export interface VisualsChoice {
  kept: VisualKeyDTO[]
  excluded: VisualKeyDTO[]
}

/** The identity triple of a visual, without its URLs or labels. */
export const visualKeyOf = ({ assignmentPublicId, filename, visualType }: VisualKeyDTO): VisualKeyDTO =>
  ({ assignmentPublicId, filename, visualType })

/** One string per visual, for sets and React keys. */
export const visualIdOf = ({ assignmentPublicId, visualType, filename }: VisualKeyDTO) =>
  `${assignmentPublicId}/${visualType}/${filename}`

/**
 * The rooms of the room filter, in the order of the backend's `RoomLabel` (ISSUE-3636), which is prototype v3's
 * `ROOMS` with the bathroom and "other" added. A label outside it (a newer backend) comes after, as first seen.
 */
export const CAMPAIGN_ROOM_ORDER: readonly string[] = [
  'EXTERIOR',
  'LIVING_ROOM',
  'KITCHEN',
  'DINING',
  'BEDROOM',
  'BATHROOM',
  'STUDY',
  'WHOLE_HOUSE',
  'OTHER',
]

export interface CampaignRoom {
  room: string
  count: number
}

/**
 * The rooms a kind's visuals show, with their counts. Visuals not classified yet (no room) are left out: they show
 * under "All rooms" only. The dialog lists the rooms only when there are two or more (prototype v3).
 */
export function roomsOf(items: Pick<ContentItemDTO, 'roomLabel'>[]): CampaignRoom[] {
  const counts = new Map<string, number>()
  items.forEach(({ roomLabel }) => {
    if (roomLabel) counts.set(roomLabel, (counts.get(roomLabel) ?? 0) + 1)
  })
  const rank = (room: string) => {
    const index = CAMPAIGN_ROOM_ORDER.indexOf(room)
    return index === -1 ? CAMPAIGN_ROOM_ORDER.length : index
  }
  // Array.prototype.sort is stable: rooms outside the order keep the order they were first seen in
  return Array.from(counts, ([room, count]) => ({ room, count })).sort((a, b) => rank(a.room) - rank(b.room))
}

/** The visuals the room filter shows; `null` is "All rooms". */
export const visualsInRoom = <T extends Pick<ContentItemDTO, 'roomLabel'>>(items: T[], room: string | null) =>
  room === null ? items : items.filter((item) => item.roomLabel === room)

/**
 * What the dialog starts from: the kind's visuals not left out when the campaign draws on the kind, none when the kind
 * is left out (prototype v3 `openBucket`: keeping one then brings the kind back).
 */
export function keptAtOpening(items: VisualKeyDTO[], excludedVisuals: VisualKeyDTO[], included: boolean): ReadonlySet<string> {
  if (!included) return new Set()
  const leftOut = new Set(excludedVisuals.map(visualIdOf))
  return new Set(items.map(visualIdOf).filter((id) => !leftOut.has(id)))
}

/** A tile pressed: kept becomes left out and the other way round. */
export function toggleKept(kept: ReadonlySet<string>, id: string): ReadonlySet<string> {
  const next = new Set(kept)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  return next
}

/** Whether every visual the filter shows is kept: the foot then offers "Leave all out", else "Keep all". */
export const allKept = (kept: ReadonlySet<string>, shown: VisualKeyDTO[]) => shown.every((item) => kept.has(visualIdOf(item)))

/** "Keep all" / "Leave all out": acts on the visuals the room filter shows, the others keep their mark. */
export function setKept(kept: ReadonlySet<string>, shown: VisualKeyDTO[], on: boolean): ReadonlySet<string> {
  const next = new Set(kept)
  shown.forEach((item) => {
    if (on) next.add(visualIdOf(item))
    else next.delete(visualIdOf(item))
  })
  return next
}

/** Photos keep at least one: every post ends on the house (prototype v3: Save disabled, the count says why). */
export const keepsTooFew = (kind: ContentKind, kept: ReadonlySet<string>) => kind === ContentKind.PHOTOS && kept.size === 0

export const sameKept = (a: ReadonlySet<string>, b: ReadonlySet<string>) => a.size === b.size && Array.from(a).every((id) => b.has(id))

/** The choice to save, in delivery order, as identity triples only. */
export function visualsChoice(items: VisualKeyDTO[], kept: ReadonlySet<string>): VisualsChoice {
  return {
    kept: items.filter((item) => kept.has(visualIdOf(item))).map(visualKeyOf),
    excluded: items.filter((item) => !kept.has(visualIdOf(item))).map(visualKeyOf),
  }
}

/** The visuals of a kind in the viewer's content inventory, in delivery order. */
export const visualsOfKind = (inventory: { kinds: ContentKindInventoryDTO[] } | undefined, kind: ContentKind) =>
  inventory?.kinds.find((entry) => entry.kind === kind)?.items ?? []

/**
 * The visuals left out as the page shows them: the campaign's, with the choices on their way applied (a visual a
 * choice keeps is taken out, one it leaves out is added once).
 */
export function excludedWithChoices(excludedVisuals: VisualKeyDTO[], choices: VisualsChoice[]): VisualKeyDTO[] {
  if (!choices.length) return excludedVisuals
  const kept = new Set(choices.flatMap((choice) => choice.kept.map(visualIdOf)))
  const shown = excludedVisuals.filter((visual) => !kept.has(visualIdOf(visual)))
  const ids = new Set(shown.map(visualIdOf))
  choices.forEach((choice) => choice.excluded.forEach((visual) => {
    if (ids.has(visualIdOf(visual))) return
    ids.add(visualIdOf(visual))
    shown.push(visual)
  }))
  return shown
}
